import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { AuthService } from '../@services/auth.service';
import { ToastService } from '../@services/toast.service';
import { llog } from '../@shared/helpers';
import _ from 'lodash';
import { DataAgreementsService } from '../@services/data-agreements.service';
import { DataSubscriptionsService } from '../@services/data-subscriptions.service';
import { DateTime } from 'luxon';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { UtpPlanAgreementComponent } from './legal/utp-plan-agreement.component';
import { NyseExhibitBComponent } from './legal/nyse-exhibit-b.component';
import { OpraAttachmentB1Component } from './legal/opra-attachment-b1.component';


@Component({
  selector: 'app-data-agreements',
  imports: [
    CommonModule,
    FormsModule,
    UtpPlanAgreementComponent,
    NyseExhibitBComponent,
    OpraAttachmentB1Component,
  ],
  templateUrl: './data-agreements.component.html',
  styleUrl: './data-agreements.component.scss'
})
export class DataAgreementsComponent implements OnInit {
  cid: string = '';
  customers: any = [];
  has_customer: boolean = true;
  show_agreement: boolean = false;
  show_dashboard: boolean = false;
  subscription_required: boolean = false;

  email_known: boolean = false;
  email_submitted: boolean = false;
  user_email: any;
  verification_code: any;
  // Failed-verification gate: after MAX_VERIFY_ATTEMPTS bad codes, the page
  // refuses further verification attempts and surfaces a contact-admin
  // message. Reset on successful verification or page reload. Aligns with
  // the contract: "if they can't pass that stage the page denies access."
  verify_attempts: number = 0;
  verify_denied: boolean = false;
  readonly MAX_VERIFY_ATTEMPTS = 5;

  customer_name: string = '';
  customer_string: string = '';
  show_forms: boolean = false;
  viewed_forms: boolean = false;
  scrolled_to_bottom: boolean = false;
  can_save_changes: boolean = false;

  // Exposed for template binding — vendor strings interpolated into
  // the OPRA Attachment B-1 (and NYSE Exhibit B vendor name) agreements.
  // TLD-derived in environment.ts so .com / .ai builds pick up the right host.
  env = environment;

  subscription_plans: any = [];
  switch_prompt = false;
  new_plan: any = null;

  agreement: any = {
    outside_us: 'no',
    employer_outside_us: 'no',
    agree_to_forms: false,
    agree_to_terms: false,
    agree_to_notify: false,
    agree_to_signature: false,
    electronic_signature: null,
    first_name: null,
    last_name: null,
    email: null,
    phone: null,
    address: null,
    city: null,
    state: null,
    country: null,
    zip_code: null,
    employment_status: null,
    employer: {
      name: null,
      address: null,
      city: null,
      state: null,
      zip_code: null,
      country: null
    },
    occupation: null,
    job_title: null,
    employment_function: null,
    other_employment: null,
    questions: {
      personal_use: undefined,
      business_use: undefined,
      registered_sec: undefined,
      registered_agency: undefined,
      perform_functions: undefined,
      investment_advice: undefined,
      asset_manager: undefined,
      capital_usage: undefined,
      entity_trading: undefined,
      profit_sharing: undefined,
      benefits_exchange: undefined
    }
  };


  constructor(
    public authService: AuthService,
    private toast: ToastService,
    private daService: DataAgreementsService,
    private dsService: DataSubscriptionsService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  // Customer display name + UX-friendly noun phrase resolved from the
  // backend brand-config (Customers.name) when accessed via a customer-
  // owned host. Falls back to the hardcoded switch in get_user_customers
  // when brand-config doesn't return a name (e.g. legacy ?customer= path).
  private _customer_name_from_host: string = '';

  async ngOnInit() {
    // Customer resolution priority:
    //   1. ?customer= query param (legacy explicit path — set by SPAs
    //      that link to this page directly via exchAgreementsUrl helper)
    //   2. Hostname lookup (when accessed via a customer-owned domain,
    //      e.g. scanners.theotrade.com/data-agreements via Vercel rewrite)
    // Hostname lookup runs only when (1) is absent, so explicit always wins.
    this.cid = this.router.routerState.snapshot.root.queryParams['customer'] || '';
    if (!this.cid && typeof window !== 'undefined') {
      const brand = await this.daService.getBrandByHost(window.location.hostname);
      if (brand?.slug) {
        this.cid = String(brand.slug);
        this._customer_name_from_host = String(brand.name || '');
      }
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true
    });
    this.get_user_agreement();
    this.get_user_customers();
  }

  async on_customer_change(event: any) {
    await this.get_user_agreement();
  }

  // Get user agreement data for selected customer
  async get_user_agreement() {
    if (!this.cid) return;
    await this.get_user_customers();

    this.email_known = false;
    this.can_save_changes = false;
    this.show_dashboard = false;
    this.show_agreement = false;

    let res: any = await this.daService.getUserAgreements(this.cid);
    if (res && !res.err) {
      this.agreement = res;
      if (res.email) this.email_known = true;
      if (res.agree_to_forms && res.agree_to_terms && res.agree_to_notify && res.agree_to_signature) this.can_save_changes = true;
      if (!res.completed) this.show_agreement = true;
      //if (res.pro_access) this.subscription_required = true;
      if (!res.pro_access) this.subscription_required = false;
    }

    /*
    if (this.subscription_required) {
      await this.get_user_subscriptions();
    }
    */

    this.show_dashboard = true;
  }

  // Get customers assigned to user
  async get_user_customers() {
    let customers = this.authService.get_customers();
    this.customers = customers;
    if (this.cid === 'qpp' && customers.includes('delphi-sig')) {
      this.has_customer = true;
    }
    else if (this.cid && !this.authService.is_admin() && !customers.includes(this.cid)) {
      this.has_customer = false;
    }

    switch(this.cid) {
      case 'option-pit':
        this.customer_name = 'Option Pit';
        this.customer_string = 'an Option Pit';
        break;
      case 'strat-alerts':
        this.customer_name = 'Strat Alerts';
        this.customer_string = 'a Strat Alerts';
        break;
      case 'theo-trade':
        this.customer_name = 'TheoTrade';
        this.customer_string = 'a TheoTrade';
        break;
      case 'mta':
        this.customer_name = 'Monument Traders Alliance';
        this.customer_string = 'a Monument Traders Alliance';
        break;
      case 'alpha-shark':
        this.customer_name = 'AlphaShark';
        this.customer_string = 'an AlphaShark';
        break;
      case 'delphi-sig':
        this.customer_name = 'Delphi Signal';
        this.customer_string = 'a Delphi Signal';
        break;
      case 'qpp':
        this.customer_name = 'Quant Pivot Pro';
        this.customer_string = 'a Quant Pivot Pro';
        break;
      case 'simpler-trading':
        this.customer_name = 'Simpler Trading';
        this.customer_string = 'a Simpler Trading';
        break;
    }
    // Brand-config-derived name (when reached via a customer-owned host)
    // wins over the hardcoded switch above. Lets new customers come online
    // by configuring Customers.name in F2-ADMIN without touching this file.
    if (this._customer_name_from_host) {
      this.customer_name = this._customer_name_from_host;
      // customer_string: simple "a/an Foo" article picker for the UI copy;
      // fall back to "a" + name if we can't easily guess.
      const first = this._customer_name_from_host.charAt(0).toLowerCase();
      const article = 'aeiou'.includes(first) ? 'an' : 'a';
      this.customer_string = `${article} ${this._customer_name_from_host}`;
    }
  }

  // Show/hide agreement form
  toggle_agreement() {
    this.show_agreement = !this.show_agreement;
  }

  // Show/hide official exchange agreements
  toggle_forms() {
    this.show_forms = !this.show_forms;
    this.viewed_forms = true;
  }

  // Toggle email submission boolean
  toggle_email_submitted() {
    this.email_submitted = !this.email_submitted;
  }

  async submit_email_for_verification() {
    this.email_submitted = true;
    let res: any = await this.daService.sendVerificationCode(this.user_email);
  }

  async validate_verification_code() {
    if (this.verify_denied) return;
    let res: any = await this.daService.validateVerificationCode(this.verification_code);
    if (!res.err) {
      this.verify_attempts = 0;
      await this.get_user_agreement();
      return;
    }
    // Failed attempt — bump counter, deny after MAX exceeded.
    this.verify_attempts++;
    if (this.verify_attempts >= this.MAX_VERIFY_ATTEMPTS) {
      this.verify_denied = true;
      this.show_agreement = false;
      this.show_dashboard = false;
      this.toast.error(
        'Access denied',
        `Too many failed verification attempts (${this.MAX_VERIFY_ATTEMPTS}). Contact support to reset.`
      );
    }
  }

  agree_to_forms() {
    this.agreement.agree_to_forms = true;
    this.show_forms = false;
    this.eligible_to_submit();
  }

  eligible_to_submit() {
    if (
      this.agreement.agree_to_forms &&
      this.agreement.agree_to_terms &&
      this.agreement.agree_to_notify &&
      this.agreement.agree_to_signature &&
      this.agreement.electronic_signature
    ) {
      this.can_save_changes = true;
    }
    else {
      this.can_save_changes = false;
    }
  }

  // Enables the "I Agree" button only after the user has scrolled to the
  // bottom of the stacked legal agreements. 4px tolerance covers sub-pixel
  // rounding on hi-DPI displays where scrollTop + clientHeight can fall
  // short of scrollHeight by a fraction.
  onAgreementsScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) {
      this.scrolled_to_bottom = true;
    }
  }

  async save_agreement(form: NgForm) {
    if (form.invalid) {
      this.toast.error('Please complete all required fields.');
      form.control.markAllAsTouched();
      return;
    }

    // Validate employment fields
    let missing_job_fields = false;
    if (this.agreement.employment_status === 'employed') {
      if (
        !this.agreement.employer.name ||
        !this.agreement.employer.address ||
        !this.agreement.employer.city ||
        !this.agreement.occupation ||
        !this.agreement.job_title ||
        !this.agreement.employment_function
      ) {
        missing_job_fields = true;
      }
    }
    else if (this.agreement.employment_status === 'self-employed') {
      if (
        !this.agreement.occupation ||
        !this.agreement.job_title ||
        !this.agreement.employment_function
      ) {
        missing_job_fields = true;
      }
    }
    else if (this.agreement.employment_status === 'other') {
      if (!this.agreement.other_employment) missing_job_fields = true;
    }

    if (this.agreement.employment_status === 'employed' && this.agreement.employer_outside_us === 'no') {
      if (!this.agreement.employer.state || !this.agreement.employer.zip_code) missing_job_fields = true;
    } 
    else if (this.agreement.employment_status === 'employed' && this.agreement.employer_outside_us === 'yes') {
      if (!this.agreement.employer.country) missing_job_fields = true;
    }


    if (missing_job_fields) {
      this.toast.error('Please complete all employment-related fields.');
      return;
    }

    // Validate address fields
    let missing_address_fields = false;

    if (this.agreement.outside_us === 'no') {
      if (!this.agreement.state || !this.agreement.zip_code) missing_address_fields = true;
    }
    else if (this.agreement.outside_us === 'yes') {
      if (!this.agreement.country) missing_address_fields = true;
    }

    if (missing_address_fields) {
      this.toast.error('Please complete all address fields.');
      return;
    }

    // Validate electronic signature
    let signature = this.agreement.electronic_signature;
    let full_name = `${this.agreement.first_name} ${this.agreement.last_name}`;
    if (signature != full_name) {
      this.toast.error('Signature does not match first and last name provided above.');
      return;
    }


    // Send updated agreement
    let res: any = await this.daService.updateUserAgreements(this.agreement);
    if (res && !res.err && res.success) {
      this.toast.success('Agreement submitted successfully.');
      await this.get_user_agreement();
      this.show_agreement = false;
      this.show_dashboard = true;
    }
  }

  async download_agreements() {
    let pdfBlob: Blob = await this.daService.downloadAgreements(this.cid);
    if (!pdfBlob) return;

    const url = window.URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;

    let pdf_name = `F2 Tech Exchange Agreements.pdf`;
    a.download = pdf_name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }
  
  // Get data subscription details for provided customer + user
  async get_user_subscriptions() {
    if (!this.cid) return;
    let res: any = await this.dsService.get_user_subscriptions(this.cid);
    if (res && !res.err) this.subscription_plans = res;
  }

  // Switch user's subscription to selected plan
  async switch_user_plan() {
    this.switch_prompt = false;

    let res: any = await this.dsService.switch_user_plan(this.new_plan, this.cid);
    
    if (res && !res.err) {
      setTimeout(() => {
        this.get_user_subscriptions();
      }, 200)
    }
  }

  // Open modal to confirm plan switch
  confirm_plan_switch(plan: any) {
    this.switch_prompt = true;
    this.new_plan = plan;
  }

  // Close modal to confirm plan switch
  close_switch_prompt() {
    this.switch_prompt = false;
    this.new_plan = null;
  }
}
